import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  Alert,
  Skeleton,
} from "@mui/material";
import {
  School as SchoolIcon,
  History,
} from "@mui/icons-material";

import { useEffect, useState } from "react";
import ClassroomSelection from "./classroom-selection";
import { useClassroomActions } from "../../hooks/useClassroomActions";
import PayoutsTable from "./payouts-table";
import { useDomainActions } from "../../hooks/useDomainActions";
import WithdrawalConfirmationDialog from "./withdrawal-confirmation-dialog";
import { Link } from "react-router-dom";
import NavBarLayout from "../../components/nav-bar-layout";
import PaginationTracker from "../../types/pagination-tracker";

function Payout() {
  const { 
    classrooms, 
    getClassrooms, 
    withdrawEarnings, 
    sendWithdrawalOtp, 
    getPayouts 
  } = useClassroomActions();

  const { bank_account, getBankAccount } = useDomainActions();
  
  const [selectedClassroomId, setSelectedClassroomId] = useState(0);

  const selectedClassroom = classrooms?.[selectedClassroomId];

  const [tracker, setTracker] = useState<PaginationTracker>({});

  const [loading, setLoading] = useState(false);

  const [processing, setProcessing] = useState(false);
  const [loadedBankAccount, setLoadedBankAccount] = useState(false);

  const [withdrawalAmount, setWithdrawalAmount] = useState(0);

  const [withdrawalDialog, setWithdrawalDialog] = useState(false);


  const handleWithdrawal = async (otp: string) => {
    setProcessing(true);
    await withdrawEarnings(selectedClassroomId, Number(withdrawalAmount + 100), otp);
    setProcessing(false);
    setWithdrawalDialog(false);
    setWithdrawalAmount(0);
  };

  const fetchPayouts = async () => {
    if (selectedClassroomId in tracker && !tracker[selectedClassroomId].canLoadMore) {
      return;
    }

    if (!(selectedClassroomId in tracker)) {
      setTracker(prev => ({
        ...prev,
        [selectedClassroomId]: {
          offset: 0,
          canLoadMore: true
        }
      }));
    }

    const offset =  tracker[selectedClassroomId] ? tracker[selectedClassroomId].offset : 0;
    
    const newLength = await getPayouts(selectedClassroomId, offset) || 0;

    setTracker(prev => ({
      ...prev,
      [selectedClassroomId]: {
        offset: offset + 50,
        canLoadMore: newLength < 50 ? false : true
      }
    }));
  };


  useEffect(() => {
    const loadData = async () => {
      if (selectedClassroomId === 0 || selectedClassroom && selectedClassroom.payouts) return;

      setLoading(true);
      await fetchPayouts();
      setLoading(false);
    };

    loadData();
  }, [selectedClassroomId]);


  useEffect(() => {
    const getBankAccountDetails = async () => {
      if (classrooms && loadedBankAccount) return;
      await getBankAccount();
      setLoadedBankAccount(true);
    };

    getBankAccountDetails();
  }, [classrooms, bank_account]);

  useEffect(() => {
    const loadClassrooms = async () => {
      if (classrooms) return;
      await getClassrooms();
    };

    loadClassrooms();
  }, [classrooms]);

  return (
    <NavBarLayout>
      {classrooms && (
        <ClassroomSelection
          classrooms={classrooms!}
          selectedClassroomId={selectedClassroomId}
          setSelectedClassroomId={setSelectedClassroomId}
        />
      )}

      {!loadedBankAccount ? (
        <Skeleton 
          variant="rectangular" 
          height={120} 
          sx={{ borderRadius: 1, my: 2 }} 
        />
      ) : (
        !bank_account ? (
          <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
            Bank Account details not added,{' '}
            <Link 
              to="/account#hub"
              style={{ 
                textDecoration: 'underline',
                color: 'inherit'
              }}
            >
              Click here
            </Link>{' '}
            to add one now
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" component="div">
              <strong>Payout Account:</strong>
              <br />
              Account Name: {bank_account.account_name}
              <br />
              Account Number: {bank_account.account_number}
              <br />
              Bank: {bank_account.bank_name}
            </Typography>
          </Alert>
        )
      )}



      
      

        {selectedClassroom ? (
          <>
            {bank_account && selectedClassroom && (
              <Box sx={{ mt: 2, mb: 2, p: 1.5, backgroundColor: '#f9f9f9', borderRadius: 2 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <TextField
                    label="Withdrawal Amount"
                    type="number"
                    placeholder="minimum ₦1000"
                    value={withdrawalAmount}
                    onChange={(e) => setWithdrawalAmount(parseInt(e.target.value))}
                    sx={{ 
                      flexGrow: 1,
                      '& .MuiOutlinedInput-root': {
                        height: '40px',
                        backgroundColor: 'white',
                        '&:hover': {
                          backgroundColor: 'white',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'white',
                        }
                      }
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                    }}
                    variant="outlined"
                    fullWidth
                    inputProps={{
                      min: 1000,
                      step: 1000
                    }}
                  />

                  <Button
                    variant="contained"
                    onClick={() => {
                      setWithdrawalDialog(true);
                      sendWithdrawalOtp(selectedClassroomId, withdrawalAmount.toLocaleString());
                    }}
                    disabled={
                      processing || 
                      !withdrawalAmount || 
                      Number(withdrawalAmount) <= 0 || 
                      Number(withdrawalAmount + 100) > (selectedClassroom?.earnings || 0) ||
                      withdrawalAmount < 1000
                    }
                    sx={{ minWidth: 120, height: 40 }}
                  >
                    {processing ? (
                      <>
                        <CircularProgress size={16} color="inherit" sx={{ mr: 1 }}/>
                      </>
                    ) : (
                      'Withdraw'
                    )}
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  ₦100 processing fee applies
                </Typography>
              </Box>
            )}
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : selectedClassroom.payouts && selectedClassroom.payouts.length > 0 ? (
              <PayoutsTable 
                selectedClassroom={selectedClassroom} 
                fetchPayouts={fetchPayouts}
                tracker={tracker}
              />
            ) : (
              <Box 
                sx={{ 
                  textAlign: "center", 
                  py: 8,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                <History
                  sx={{ 
                    fontSize: 48, 
                    mb: 2, 
                    color: 'grey.400'
                  }} 
                />
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ color: 'grey.700', fontWeight: 500 }}
                >
                  No payouts found
                </Typography>
              </Box>
            )}

          </>
        ) : (
          <Card 
            elevation={0} 
            sx={{ 
              border: "2px dashed", 
              borderColor: "divider",
              textAlign: "center",
              py: 8,
              mt: 4
            }}
          >
            <CardContent>
              <SchoolIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2, opacity: 0.5 }} />
              <Typography variant="h5" gutterBottom color="text.secondary">
                Select a Classroom
              </Typography>
              <Typography variant="body1" color="text.secondary" maxWidth={400} mx="auto">
                Choose a classroom from the dropdown above to view and manage payouts.
              </Typography>
            </CardContent>
          </Card>
        )}


      <WithdrawalConfirmationDialog
        classroom_id={selectedClassroomId}
        open={withdrawalDialog}
        setOpen={setWithdrawalDialog}
        onProceed={handleWithdrawal}
        amount={withdrawalAmount.toLocaleString()}
      />

    </NavBarLayout>
  );
}

export default Payout;